// Backend completion uses a recording UUID; the installed native bridge accepts
// only its integer database row id. Keep this mapping beyond the pending marker
// and webview lifetime so a delayed completion cannot address the wrong row.
export const NATIVE_RECORDING_IDENTITIES_KEY = "nativeRecordingIdentities";
const MAX_IDENTITIES = 512;

const isNativeRowId = (id) =>
  (typeof id === "number" || (typeof id === "string" && /^[1-9]\d*$/.test(id))) &&
  Number.isSafeInteger(Number(id)) &&
  Number(id) > 0;

const readIdentities = () => {
  try {
    const entries = JSON.parse(localStorage.getItem(NATIVE_RECORDING_IDENTITIES_KEY) || "[]");
    return Array.isArray(entries)
      ? entries.filter((entry) => entry?.userId && typeof entry.uuid === "string" && isNativeRowId(entry.recordingId))
      : [];
  } catch (_) {
    return [];
  }
};

export function rememberNativeRecordingId(recordingUuid, recordingId, userId) {
  if (!userId || typeof recordingUuid !== "string" || !recordingUuid.trim() || !isNativeRowId(recordingId)) return false;
  const entries = readIdentities();
  const existing = entries.find((entry) => entry.userId === String(userId) && entry.uuid === recordingUuid);
  if (existing && Number(existing.recordingId) !== Number(recordingId)) return false;
  // If native reuses a row, retire its old alias. A late completion must never
  // be translated to that row's new recording, including across user accounts.
  const remaining = entries.filter(
    (entry) =>
      Number(entry.recordingId) !== Number(recordingId) && !(entry.userId === String(userId) && entry.uuid === recordingUuid)
  );
  remaining.push({ userId: String(userId), uuid: recordingUuid, recordingId: Number(recordingId) });
  try {
    localStorage.setItem(NATIVE_RECORDING_IDENTITIES_KEY, JSON.stringify(remaining.slice(-MAX_IDENTITIES)));
    return true;
  } catch (_) {
    return false;
  }
}

export function resolveNativeRecordingUpdateId(recordingId) {
  // Legacy recording payloads already use a native row id. Validate the entire
  // token: parseInt("731a-uuid") would acknowledge/delete an unrelated row 731.
  if (isNativeRowId(recordingId)) return recordingId;
  try {
    const userId = localStorage.getItem("authUserId");
    if (!userId || typeof recordingId !== "string") return undefined;
    // SQLite can reuse a deleted row while a newer capture is still running.
    // Until that capture hands over its verified row id, an old UUID alias could
    // acknowledge its new audio. Read the marker directly to avoid a dependency
    // cycle with nativeRecordingMarker, which registers identities here.
    const pending = JSON.parse(localStorage.getItem("nativeActiveRecording") || "null");
    if (pending?.recordingUuid && pending.recordingUuid !== recordingId && !isNativeRowId(pending.nativeRecordingId)) {
      return undefined;
    }
    return readIdentities().find((entry) => entry.userId === userId && entry.uuid === recordingId)?.recordingId;
  } catch (_) {
    return undefined;
  }
}
